import { SaveAsTemplateDialog } from "../../components/coaching/SaveAsTemplateDialog";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Copy,
  Trash2,
  Save,
  FileText,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Table2,
  Mail,
  MailOpen,
  Lock,
  BookmarkPlus,
} from "lucide-react";
import {
  getProgram,
  updateProgram,
  addProgramWeek,
  reorderProgramWeeks,
  duplicateProgramWeek,
  deleteProgramWeek,
  getCoachingAthlete,
  getCoachingAthleteLactate,
  getWeekCompletion,
  getProgramWorkoutLogs,
  sendDigestNow,
  getDigestPreviewUrl,
} from "../../lib/api";
import WeekCanvas from "../../components/coaching/WeekCanvas";
import AssignProgramModal from "../../components/coaching/AssignProgramModal";
import { RichTextEditor } from "../../components/RichTextEditor";
import {
  weekDateRange,
  formatDateRange,
  formatDateShort,
  weekVolume,
  formatVolume,
  parseRaceDistance,
  computeWeekActuals,
  isWeekPast,
  logsByKey as buildLogsByKey,
} from "../../lib/coaching";
import { computeVdot, trainingPaces, lactateTrainingPaces } from "../../lib/vdot";
import { toast } from "sonner";

export default function ProgramEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [program, setProgram] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [athleteLactate, setAthleteLactate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeWeekIdx, setActiveWeekIdx] = useState(0);
  // Week-tab drag-to-reorder state. Holds the week_number being dragged.
  const [dragWeekNum, setDragWeekNum] = useState(null);
  const [dropTargetWeekNum, setDropTargetWeekNum] = useState(null);

  // Reorder weeks by moving `sourceWeekNum` to the slot currently held by
  // `destWeekNum` (the week the user dropped onto). The list of week_numbers
  // gets rotated and sent to the server which atomically renumbers 1..N.
  const moveWeekTo = async (sourceWeekNum, destWeekNum) => {
    if (!program || sourceWeekNum === destWeekNum) return;
    const order = program.weeks.map((w) => w.week_number);
    const fromIdx = order.indexOf(sourceWeekNum);
    const toIdx = order.indexOf(destWeekNum);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...order];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic UI: renumber locally so the tabs jump immediately.
    setProgram((p) => {
      if (!p) return p;
      const reordered = next.map((origNum, i) => {
        const w = p.weeks.find((x) => x.week_number === origNum);
        return { ...w, week_number: i + 1 };
      });
      return { ...p, weeks: reordered, total_weeks: reordered.length };
    });
    setActiveWeekIdx(toIdx);
    try {
      const fresh = await reorderProgramWeeks(id, next);
      setProgram(fresh);
      toast.success("Week order updated");
    } catch {
      toast.error("Couldn't reorder — refreshing");
      try {
        const p = await getProgram(id);
        setProgram(p);
      } catch { /* swallow */ }
    }
  };
  const [showAssign, setShowAssign] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [completion, setCompletion] = useState(null);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const initialWeekApplied = useRef(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await getProgram(id);
      setProgram(p);
      if (p.athlete_id) {
        try {
          const a = await getCoachingAthlete(p.athlete_id);
          setAthlete(a);
        } catch {
          /* ignore */
        }
        try {
          const lac = await getCoachingAthleteLactate(p.athlete_id);
          setAthleteLactate(lac || null);
        } catch {
          setAthleteLactate(null);
        }
        try {
          const logs = await getProgramWorkoutLogs(id);
          setWorkoutLogs(logs || []);
        } catch {
          setWorkoutLogs([]);
        }
      } else {
        setWorkoutLogs([]);
      }
    } catch {
      toast.error("Couldn't load program");
      navigate("/coaching");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // After program loads, jump to ?week=N if provided, otherwise default to
  // the week that contains today's date (or the closest upcoming week, or
  // the last week of the program if it's already over). This is much more
  // useful for active coaching than always opening on Week 1. One-time on
  // initial load — manual week-tab clicks won't be re-overridden.
  useEffect(() => {
    if (!program || initialWeekApplied.current) return;
    const wkParam = searchParams.get("week");
    if (wkParam) {
      const wn = parseInt(wkParam, 10);
      const idx = program.weeks.findIndex((w) => w.week_number === wn);
      if (idx >= 0) setActiveWeekIdx(idx);
    } else if (program.start_date && program.weeks?.length) {
      // Compute which 7-day window today falls in.
      try {
        const start = new Date(program.start_date + "T00:00:00");
        const today = new Date();
        // Strip time so week math is in whole days.
        today.setHours(0, 0, 0, 0);
        start.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today - start) / (1000 * 60 * 60 * 24));
        let targetIdx;
        if (daysDiff < 0) {
          // Program hasn't started yet — open Week 1.
          targetIdx = 0;
        } else {
          const wkIdx = Math.floor(daysDiff / 7);
          // Clamp to last week so a fully-past program opens on its final week.
          targetIdx = Math.min(wkIdx, program.weeks.length - 1);
        }
        setActiveWeekIdx(targetIdx);
      } catch {
        /* fall through to default 0 */
      }
    }
    initialWeekApplied.current = true;
  }, [program, searchParams]);

  // Deep-link: if the URL has `?workout={id}` (e.g. clicked from the coach
  // Recent Activity feed), scroll the matching workout card into view and
  // briefly flash a burgundy ring around it so the coach can find what
  // they came for without hunting.
  useEffect(() => {
    if (!program || !program.weeks?.length) return;
    const targetWorkoutId = searchParams.get("workout");
    if (!targetWorkoutId) return;
    // Wait a tick for the WeekCanvas to render the active week's DOM.
    const t = setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="coach-grid-workout-${targetWorkoutId}"]`
      );
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-[var(--tir-burgundy)]", "ring-offset-2");
      setTimeout(() => {
        el.classList.remove("ring-4", "ring-[var(--tir-burgundy)]", "ring-offset-2");
      }, 2200);
    }, 350);
    return () => clearTimeout(t);
  }, [program, activeWeekIdx, searchParams]);

  // Fetch Strava completion data for the active week (one-shot per week change + after autosave).
  useEffect(() => {
    if (!program || !program.weeks || !program.weeks[activeWeekIdx]) {
      setCompletion(null);
      return;
    }
    let alive = true;
    const wn = program.weeks[activeWeekIdx].week_number;
    getWeekCompletion(id, wn)
      .then((data) => {
        if (alive) setCompletion(data);
      })
      .catch(() => {
        if (alive) setCompletion(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeWeekIdx, program?.weeks?.[activeWeekIdx]?.week_number, program?.athlete_id, program?.start_date, saving]);

  // Autosave when the program changes (debounced 1.2s)
  useEffect(() => {
    if (!dirty || !program) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await persist();
    }, 1200);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, program]);

  // Poll for athlete logs every 45s so coach sees real-time completions.
  useEffect(() => {
    if (!program?.athlete_id || program?.program_type === "generic") return;
    const t = setInterval(() => {
      getProgramWorkoutLogs(id)
        .then((logs) => setWorkoutLogs(logs || []))
        .catch(() => {});
    }, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, program?.athlete_id, program?.program_type]);

  const persist = async () => {
    if (!program) return;
    setSaving(true);
    try {
      const updated = await updateProgram(id, {
        title: program.title,
        goal: program.goal,
        target_race: program.target_race,
        experience_level: program.experience_level,
        max_mileage: program.max_mileage,
        prerequisite: program.prerequisite,
        total_weeks: program.total_weeks,
        start_day: program.start_day,
        start_date: program.start_date,
        show_total_volume: program.show_total_volume,
        intro_howto: program.intro_howto,
        intro_modify: program.intro_modify,
        weeks: program.weeks,
      });
      setProgram(updated);
      setDirty(false);
    } catch (e) {
      toast.error("Autosave failed — check connection");
    } finally {
      setSaving(false);
    }
  };

  const updateProgramField = (key, value) => {
    setProgram((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const updateWeek = (idx, updatedWeek) => {
    setProgram((p) => {
      const weeks = p.weeks.map((w, i) => (i === idx ? updatedWeek : w));
      return { ...p, weeks };
    });
    setDirty(true);
  };

  // Copy a single workout (with fresh UUIDs) into the matching day-labels of
  // each target week. Used by the "Duplicate to days…" picker when the coach
  // ticks one or more weeks beyond the current one. Day labels (Mon/Tue/…)
  // are used instead of day IDs because IDs differ between weeks but labels
  // are the canonical key.
  const crossWeekDuplicate = (sourceWorkout, dayLabels, weekNumbers, opts = {}) => {
    if (!sourceWorkout || !dayLabels?.length || !weekNumbers?.length) return;
    const asAlt = !!opts.asAlternative;
    const newId = () =>
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now();
    const reidSeg = (seg) => ({
      ...seg,
      id: newId(),
      items: (seg.items || []).map(reidSeg),
    });
    const cloneWorkout = () => ({
      ...sourceWorkout,
      id: newId(),
      is_alternative: asAlt,
      segments: (sourceWorkout.segments || []).map(reidSeg),
      strength_blocks: (sourceWorkout.strength_blocks || []).map((b) => ({
        ...b,
        id: newId(),
        exercises: (b.exercises || []).map((ex) => ({ ...ex, id: newId() })),
      })),
    });
    setProgram((p) => {
      const targetSet = new Set(weekNumbers);
      const labelSet = new Set(dayLabels);
      const weeks = p.weeks.map((w) => {
        if (!targetSet.has(w.week_number)) return w;
        return {
          ...w,
          days: (w.days || []).map((d) =>
            labelSet.has(d.label)
              ? { ...d, workouts: [...(d.workouts || []), cloneWorkout()] }
              : d
          ),
        };
      });
      return { ...p, weeks };
    });
    setDirty(true);
  };

  const onAddWeek = async () => {
    try {
      // save pending edits first
      if (dirty) await persist();
      const p = await addProgramWeek(id);
      setProgram(p);
      setActiveWeekIdx(p.weeks.length - 1);
      toast.success("Week added");
    } catch {
      toast.error("Couldn't add week");
    }
  };

  const onDuplicateWeek = async () => {
    if (!program) return;
    const num = program.weeks[activeWeekIdx]?.week_number;
    if (!num) return;
    try {
      if (dirty) await persist();
      const p = await duplicateProgramWeek(id, num);
      setProgram(p);
      setActiveWeekIdx(p.weeks.length - 1);
      toast.success("Week duplicated");
    } catch {
      toast.error("Couldn't duplicate week");
    }
  };

  const onDeleteWeek = async () => {
    if (!program) return;
    const num = program.weeks[activeWeekIdx]?.week_number;
    if (!num) return;
    if (!window.confirm(`Delete week ${num}?`)) return;
    try {
      await deleteProgramWeek(id, num);
      await load();
      setActiveWeekIdx((i) => Math.max(0, i - 1));
      toast.success("Week deleted");
    } catch {
      toast.error("Couldn't delete week");
    }
  };

  // Pace prescription source: prefer real lactate test data when present, fall
  // back to VDOT-derived Daniels paces. Both produce the same shape, so the
  // segment editor and time estimates work identically.
  const athletePaces = useMemo(() => {
    const lacPaces = lactateTrainingPaces(athleteLactate);
    if (lacPaces) return lacPaces;
    const pp = athlete?.pace_profile;
    if (!pp) return null;
    const vdot = computeVdot(pp.race_distance_m, pp.race_time_seconds);
    return trainingPaces(vdot);
  }, [athleteLactate, athlete?.pace_profile]);
  const usingLactatePaces = !!lactateTrainingPaces(athleteLactate);
  // Compact "where did these paces come from?" descriptor that the workout
  // editor renders as a small badge next to the title. Coaches who haven't
  // booked a lactate test for the athlete see a "VDOT estimate" nudge,
  // which doubles as a hint to book one.
  const paceSource = useMemo(() => {
    if (usingLactatePaces) {
      let date = null;
      const raw = athleteLactate?.test_date;
      if (raw) {
        try {
          const d = new Date(raw + (raw.length === 10 ? "T00:00:00" : ""));
          if (!isNaN(d.getTime())) {
            date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
          }
        } catch { /* ignore */ }
      }
      return { kind: "lactate", date };
    }
    if (athlete?.pace_profile && athlete.pace_profile.race_time_seconds) {
      return { kind: "vdot" };
    }
    return { kind: null };
  }, [usingLactatePaces, athleteLactate?.test_date, athlete?.pace_profile]);

  // Athlete actuals for the active week (planned vs actual + completed/missed).
  const logsLookup = useMemo(() => buildLogsByKey(workoutLogs), [workoutLogs]);
  const weekActuals = useMemo(() => {
    if (!program) return null;
    const w = program.weeks?.[activeWeekIdx];
    if (!w) return null;
    // Match the headline "week volume" by also including any race that
    // falls within this week's calendar range.
    const [ws, we] = weekDateRange(program.start_date, w.week_number);
    const racesInWeek = (athlete?.races || []).filter(
      (r) => r.date && ws && we && r.date >= ws && r.date <= we
    );
    return computeWeekActuals(w, logsLookup, {
      isPast: isWeekPast(program, w),
      racesInWeek,
      athletePaces,
    });
  }, [program, activeWeekIdx, logsLookup, athlete?.races, athletePaces]);

  if (loading || !program) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading program…
      </div>
    );
  }

  const week = program.weeks[activeWeekIdx];
  const isGeneric = program.program_type === "generic";

  const backLink = isGeneric
    ? "/coaching/library"
    : athlete
    ? `/coaching/athletes/${athlete.id}`
    : "/coaching";
  const backLabel = isGeneric
    ? "Program library"
    : athlete
    ? athlete.name
    : "Coaching";

  return (
    <div
      className="max-w-7xl mx-auto px-6 lg:px-10 py-8"
      data-testid="program-editor-page"
    >
      <Link
        to={backLink}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-4"
      >
        <ArrowLeft size={14} /> {backLabel}
      </Link>

      {/* Program header */}
      <header className="border-b border-[var(--tir-ink)] pb-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
              Coaching · {isGeneric ? "Library program" : "Program editor"}
            </div>
            <input
              value={program.title}
              onChange={(e) => updateProgramField("title", e.target.value)}
              data-testid="program-title-field"
              className="font-serif-display text-2xl sm:text-4xl md:text-5xl leading-tight text-[var(--tir-ink)] bg-transparent focus:outline-none w-full"
            />
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm">
              <label className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                  Goal
                </span>
                <input
                  value={program.goal || ""}
                  onChange={(e) => updateProgramField("goal", e.target.value)}
                  className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)] flex-1 sm:flex-none sm:min-w-[180px]"
                />
              </label>
              {isGeneric ? (
                <>
                  <label className="flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Level
                    </span>
                    <select
                      value={program.experience_level || ""}
                      onChange={(e) =>
                        updateProgramField("experience_level", e.target.value || null)
                      }
                      data-testid="program-level-select"
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)]"
                    >
                      <option value="">—</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Peak mileage
                    </span>
                    <input
                      value={program.max_mileage || ""}
                      onChange={(e) =>
                        updateProgramField("max_mileage", e.target.value)
                      }
                      placeholder="55 km/week"
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)] flex-1 sm:flex-none sm:min-w-[140px]"
                    />
                  </label>
                  <label className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Prerequisite
                    </span>
                    <input
                      value={program.prerequisite || ""}
                      onChange={(e) =>
                        updateProgramField("prerequisite", e.target.value)
                      }
                      placeholder="30 km/week base"
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)] flex-1 sm:flex-none sm:min-w-[180px]"
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Target race
                    </span>
                    <input
                      value={program.target_race || ""}
                      onChange={(e) => updateProgramField("target_race", e.target.value)}
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)] flex-1 sm:flex-none sm:min-w-[180px]"
                    />
                  </label>
                  <label className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Start date
                    </span>
                    <input
                      type="date"
                      value={program.start_date || ""}
                      onChange={(e) =>
                        updateProgramField("start_date", e.target.value || null)
                      }
                      data-testid="program-start-date-input"
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)] font-mono-tir flex-1 sm:flex-none"
                    />
                  </label>
                  <label className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      Week starts on
                    </span>
                    <select
                      value={program.start_day || "mon"}
                      onChange={(e) => updateProgramField("start_day", e.target.value)}
                      data-testid="program-start-day-input"
                      className="bg-transparent border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none text-[var(--tir-ink)]"
                    >
                      <option value="mon">Monday (Mon–Sun)</option>
                      <option value="sun">Sunday (Sun–Sat)</option>
                      <option value="day1">Day 1 – Day 7</option>
                    </select>
                  </label>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]" data-testid="autosave-status">
              {saving ? "Saving…" : dirty ? "Unsaved changes" : "Saved"}
            </div>
            <button
              type="button"
              onClick={async () => {
                if (dirty) await persist();
                navigate(`/coaching/programs/${id}/planner`);
              }}
              data-testid="open-planner-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Table2 size={13} /> Planner
            </button>
            {!isGeneric && (
              <button
                type="button"
                onClick={async () => {
                  if (dirty) await persist();
                  setShowSaveTemplate(true);
                }}
                data-testid="save-as-template-btn"
                title="Copy this program (or a week range) into your library as a reusable template"
                className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
              >
                <BookmarkPlus size={13} /> Save as template
              </button>
            )}
            {isGeneric && (
              <button
                type="button"
                onClick={async () => {
                  if (dirty) await persist();
                  setShowAssign(true);
                }}
                data-testid="assign-program-btn"
                className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
              >
                <UserPlus size={13} /> Assign
              </button>
            )}
            <button
              type="button"
              onClick={persist}
              disabled={!dirty || saving}
              data-testid="manual-save-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-40"
            >
              <Save size={13} /> Save
            </button>
          </div>
        </div>

        <div className="mt-6">
          {(() => {
            // Default state: collapsed if the program already has intro
            // content (the coach has filled it in and wants to skip
            // past), expanded if both fields are empty (new program —
            // setup case where the coach needs to type something).
            //
            // Local persistence per-program in localStorage so the
            // coach's explicit choice sticks across reloads — if they
            // expand once and start editing they don't want it to
            // collapse out from under them mid-edit.
            return <IntroPagesSection program={program} updateProgramField={updateProgramField} />;
          })()}
        </div>
      </header>

      {/* Week selector — single horizontal scrollable row */}
      <section className="mb-6 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setActiveWeekIdx((i) => Math.max(0, i - 1))}
          disabled={activeWeekIdx === 0}
          className="h-9 w-9 border border-[var(--tir-border)] bg-white hover:border-[var(--tir-ink)] disabled:opacity-30 flex items-center justify-center shrink-0"
          aria-label="Previous week"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 min-w-0 overflow-x-auto">
          <div className="flex gap-1.5 items-center pb-1">
            {program.weeks.map((w, i) => {
              const [ws] = weekDateRange(program.start_date, w.week_number);
              const dateLabel = ws ? formatDateShort(ws) : null;
              const isActive = i === activeWeekIdx;
              const isRecovery = !!w.is_recovery;
              const isDone = !!w.planning_complete;
              let cls;
              if (isActive) {
                cls = isRecovery
                  ? "bg-[var(--tir-navy)] text-white border-[var(--tir-navy)]"
                  : "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]";
              } else if (isDone) {
                cls = "bg-[#eaf3ec] border-[#9bc6a8] text-[#2f7d4a] hover:border-[#2f7d4a]";
              } else if (isRecovery) {
                cls = "bg-[#e8eef2] border-[#b7d4e0] text-[var(--tir-navy)] hover:border-[var(--tir-navy)]";
              } else {
                cls = "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-ink)]";
              }
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setActiveWeekIdx(i)}
                  draggable
                  onDragStart={(e) => {
                    setDragWeekNum(w.week_number);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", `wt-${w.week_number}`); } catch { /* ignore */ }
                  }}
                  onDragEnd={() => {
                    setDragWeekNum(null);
                    setDropTargetWeekNum(null);
                  }}
                  onDragOver={(e) => {
                    if (dragWeekNum != null && dragWeekNum !== w.week_number) {
                      e.preventDefault();
                      setDropTargetWeekNum(w.week_number);
                    }
                  }}
                  onDragLeave={() => {
                    if (dropTargetWeekNum === w.week_number) setDropTargetWeekNum(null);
                  }}
                  onDrop={(e) => {
                    if (dragWeekNum == null || dragWeekNum === w.week_number) return;
                    e.preventDefault();
                    const src = dragWeekNum;
                    setDragWeekNum(null);
                    setDropTargetWeekNum(null);
                    moveWeekTo(src, w.week_number);
                  }}
                  data-testid={`week-tab-${w.week_number}`}
                  title={isRecovery ? "Recovery week — drag to reorder" : "Drag to reorder"}
                  className={`px-3 h-9 text-xs tracking-[0.2em] uppercase font-semibold border transition-colors inline-flex items-center gap-1.5 shrink-0 cursor-grab active:cursor-grabbing ${
                    dragWeekNum === w.week_number ? "opacity-40" : ""
                  } ${
                    dropTargetWeekNum === w.week_number && dragWeekNum !== w.week_number
                      ? "ring-2 ring-[var(--tir-burgundy)] ring-offset-1"
                      : ""
                  } ${cls}`}
                >
                  <span>W{w.week_number}</span>
                  {isDone && (
                    <span
                      className="text-[10px] leading-none"
                      aria-label="planning complete"
                      title="Week marked as done"
                    >
                      ✓
                    </span>
                  )}
                  {isRecovery && (
                    <span
                      className="text-[8px] tracking-[0.22em] font-bold opacity-80"
                      aria-label="recovery"
                    >
                      ↓
                    </span>
                  )}
                  {dateLabel && (
                    <span className="text-[9px] opacity-70 font-normal tracking-[0.1em]">
                      {dateLabel}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setActiveWeekIdx((i) => Math.min(program.weeks.length - 1, i + 1))}
          disabled={activeWeekIdx >= program.weeks.length - 1}
          className="h-9 w-9 border border-[var(--tir-border)] bg-white hover:border-[var(--tir-ink)] disabled:opacity-30 flex items-center justify-center shrink-0"
          aria-label="Next week"
        >
          <ChevronRight size={16} />
        </button>
        <div className="ml-auto flex items-center gap-3 shrink-0 w-full sm:w-auto justify-end pt-2 sm:pt-0 border-t sm:border-t-0 border-[var(--tir-border)]/50">
          <button
            type="button"
            onClick={onAddWeek}
            data-testid="add-week-btn"
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
          >
            <Plus size={14} /> Week
          </button>
          <button
            type="button"
            onClick={onDuplicateWeek}
            data-testid="duplicate-week-btn"
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
          >
            <Copy size={14} /> Duplicate
          </button>
          <button
            type="button"
            onClick={onDeleteWeek}
            data-testid="delete-week-btn"
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
          >
            <Trash2 size={14} /> Delete week
          </button>
        </div>
      </section>

      {/* Week header fields */}
      {week && (
        <>
          <section className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4 border border-[var(--tir-border)] bg-white p-5">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Week number
              </label>
              <div className="font-serif-display text-3xl text-[var(--tir-ink)]">
                Week {week.week_number}
              </div>
              {(() => {
                const [ws, we] = weekDateRange(program.start_date, week.week_number);
                return ws && we ? (
                  <div className="text-xs text-[var(--tir-muted)] font-mono-tir mt-1" data-testid="week-date-range">
                    {formatDateRange(ws, we)}
                  </div>
                ) : (
                  <div className="text-[10px] text-[var(--tir-muted)] italic mt-1">
                    Set a program start date to see week dates
                  </div>
                );
              })()}
              {(() => {
                const v = weekVolume(week);
                // Add any race that lands in this week's date range to the
                // volume. Uses the shared `parseRaceDistance` helper so a
                // bare number ("28") is correctly read as 28 km, common
                // aliases ("Half", "Marathon") work, and time is estimated
                // at the athlete's race pace.
                const [ws, we] = weekDateRange(program.start_date, week.week_number);
                if (ws && we && athlete?.races?.length) {
                  for (const r of athlete.races) {
                    if (!r.date || r.date < ws || r.date > we) continue;
                    const parsed = parseRaceDistance(r.distance || r.name, athletePaces);
                    if (parsed.km > 0) {
                      v.km += parsed.km;
                      v.minutes += parsed.minutes;
                    }
                  }
                }
                const label = formatVolume(v);
                return label ? (
                  <div
                    className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] mt-2"
                    data-testid="week-volume"
                  >
                    {label}
                  </div>
                ) : null;
              })()}
              {completion && completion.available && completion.prescribed > 0 && (
                <div
                  className="text-[10px] tracking-[0.18em] uppercase font-semibold mt-1.5 inline-flex items-center gap-1.5"
                  data-testid="week-completion-badge"
                  title={`${completion.matched} matched · ${completion.partial} partial · ${completion.missed} missed`}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{
                      background:
                        completion.completion_pct >= 0.8
                          ? "#2f7d4a"
                          : completion.completion_pct >= 0.5
                          ? "#e6a35a"
                          : "var(--tir-burgundy)",
                    }}
                  />
                  <span className="text-[var(--tir-ink)]">
                    {Math.round(completion.completion_pct * 100)}% complete
                  </span>
                  <span className="text-[var(--tir-muted)] font-normal">
                    ({completion.matched}/{completion.prescribed})
                  </span>
                </div>
              )}
              {completion && !completion.available && completion.reason && (
                <div
                  className="text-[10px] italic text-[var(--tir-muted)] mt-1.5"
                  data-testid="week-completion-unavailable"
                >
                  {completion.reason}
                </div>
              )}
              {/* Athlete-self-logged actuals (from athlete portal) */}
              {!isGeneric && weekActuals && weekActuals.planned.count > 0 && (
                <div
                  className="mt-2 pt-2 border-t border-[var(--tir-border)] text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] flex flex-wrap items-center gap-x-3 gap-y-1"
                  data-testid="week-athlete-actuals"
                >
                  <span className="text-[var(--tir-navy)]">Athlete log:</span>
                  {weekActuals.planned.km > 0.05 && (
                    <span className="text-[var(--tir-ink)] font-mono-tir font-normal normal-case tracking-normal">
                      {weekActuals.actual.km.toFixed(weekActuals.actual.km >= 10 ? 0 : 1)} /{" "}
                      {weekActuals.planned.km.toFixed(weekActuals.planned.km >= 10 ? 0 : 1)} km
                    </span>
                  )}
                  {weekActuals.planned.minutes > 0.5 && (
                    <span className="text-[var(--tir-ink)] font-mono-tir font-normal normal-case tracking-normal">
                      {Math.round(weekActuals.actual.minutes)} / {Math.round(weekActuals.planned.minutes)} min
                    </span>
                  )}
                  <span className="text-emerald-700">{weekActuals.actual.complete}✓</span>
                  {weekActuals.actual.partial > 0 && (
                    <span className="text-amber-700">{weekActuals.actual.partial}~</span>
                  )}
                  {weekActuals.actual.skipped + weekActuals.actual.missed > 0 && (
                    <span className="text-[var(--tir-burgundy)]">
                      {weekActuals.actual.skipped + weekActuals.actual.missed}✗
                    </span>
                  )}
                  <span className="text-[var(--tir-muted)] font-normal">
                    of {weekActuals.planned.count} ({Math.round((weekActuals.actual.completion_pct || 0) * 100)}%)
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Phase label (optional)
              </label>
              <input
                value={week.phase_label || ""}
                onChange={(e) =>
                  updateWeek(activeWeekIdx, { ...week, phase_label: e.target.value })
                }
                data-testid="phase-label-input"
                placeholder="Phase 2 — Hill Stamina"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-base focus:outline-none"
              />
            </div>
            <div className="flex items-end gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm text-[var(--tir-ink)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!week.is_recovery}
                  onChange={(e) =>
                    updateWeek(activeWeekIdx, { ...week, is_recovery: e.target.checked })
                  }
                  data-testid="recovery-week-toggle"
                  className="h-4 w-4 accent-[var(--tir-burgundy)]"
                />
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
                  Recovery week
                </span>
              </label>
              <label
                className="inline-flex items-center gap-2 text-sm text-[var(--tir-ink)] cursor-pointer"
                title="Mark this week as done editing — your planning overlay, never shown to the athlete"
              >
                <input
                  type="checkbox"
                  checked={!!week.planning_complete}
                  onChange={(e) =>
                    updateWeek(activeWeekIdx, { ...week, planning_complete: e.target.checked })
                  }
                  data-testid="planning-complete-toggle"
                  className="h-4 w-4 accent-[#2f7d4a]"
                />
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
                  <span className={week.planning_complete ? "text-[#2f7d4a]" : ""}>
                    ✓ Done editing
                  </span>
                </span>
              </label>
              <label
                className="inline-flex items-center gap-2 text-sm text-[var(--tir-ink)] cursor-pointer"
                title="Show the app-wide Warm-up + Cool-down quick links above this week on the athlete portal. Uncheck when you've programmed a custom warm-up for this week."
              >
                <input
                  type="checkbox"
                  checked={week.show_warmup_cooldown_links !== false}
                  onChange={(e) =>
                    updateWeek(activeWeekIdx, { ...week, show_warmup_cooldown_links: e.target.checked })
                  }
                  data-testid="warmup-cooldown-links-toggle"
                  className="h-4 w-4 accent-[var(--tir-burgundy)]"
                />
                <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
                  Show warm-up + cool-down links
                </span>
              </label>
            </div>
            <StepGoalControls
              week={week}
              onChange={(next) => updateWeek(activeWeekIdx, { ...week, step_goal: next })}
            />
            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Coach's note for this week
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--tir-muted)]/70 font-normal italic">
                    — visible to athlete
                  </span>
                </label>
                <textarea
                  value={week.coach_note || ""}
                  onChange={(e) =>
                    updateWeek(activeWeekIdx, { ...week, coach_note: e.target.value })
                  }
                  rows={2}
                  data-testid="week-note-input"
                  placeholder="This is your bounce-back week. Focus on recovery, sleep, and fueling."
                  className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-base focus:outline-none resize-y"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  <span className="inline-flex items-center gap-1.5">
                    <Lock size={11} /> Private note
                  </span>
                  <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--tir-muted)]/70 font-normal italic">
                    — only you see this
                  </span>
                </label>
                <textarea
                  value={week.coach_private_note || ""}
                  onChange={(e) =>
                    updateWeek(activeWeekIdx, { ...week, coach_private_note: e.target.value })
                  }
                  rows={2}
                  data-testid="week-private-note-input"
                  placeholder="e.g. speed audio W5 4×90:30  ·  use Strength Block B"
                  className="w-full bg-[#FAF7F2]/50 border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-navy)] py-1 px-2 text-base focus:outline-none resize-y"
                />
              </div>
            </div>
          </section>

          {/* Week canvas */}
          <WeekCanvas
            week={week}
            onChange={(updated) => updateWeek(activeWeekIdx, updated)}
            completion={completion}
            athletePaces={athletePaces}
            paceSource={paceSource}
            weekStartDate={weekDateRange(program.start_date, week.week_number)[0]}
            races={athlete?.races || []}
            logsLookup={logsLookup}
            startDay={program.start_day || "mon"}
            athleteId={isGeneric ? null : program.athlete_id}
            programId={program.id}
            allWeeks={program.weeks.map((w) => ({
              week_number: w.week_number,
              phase_label: w.phase_label || "",
              start_date: weekDateRange(program.start_date, w.week_number)[0],
            }))}
            currentWeekNumber={week.week_number}
            onCrossWeekDuplicate={crossWeekDuplicate}
            onLogChanged={() =>
              getProgramWorkoutLogs(id)
                .then(setWorkoutLogs)
                .catch(() => {})
            }
          />

          {/* Actions */}
          <div className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-[var(--tir-border)] pt-6">
            {!isGeneric && (
              <button
                type="button"
                onClick={async () => {
                  if (dirty) await persist();
                  // Open the rendered digest HTML in a new tab. The endpoint
                  // requires coach auth, but the cookie is samesite=lax so
                  // top-level navigation in a new window will include it.
                  window.open(
                    getDigestPreviewUrl(id, week.week_number),
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
                data-testid="preview-digest-btn"
                className="inline-flex items-center gap-2 border border-[var(--tir-border)] text-[var(--tir-ink)] bg-white px-4 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:border-[var(--tir-ink)] hover:bg-[var(--tir-ivory)] transition-colors"
                title="Open the email exactly as the athlete will see it (read-only — does not send)"
              >
                <MailOpen size={14} /> Preview email
              </button>
            )}
            {!isGeneric && (
              <button
                type="button"
                onClick={async () => {
                  if (dirty) await persist();
                  if (!athlete?.email) {
                    toast.error("Add an email to this athlete first");
                    return;
                  }
                  if (!window.confirm(
                    `Send the Week ${week.week_number} preview email to ${athlete.email} now?\n\n(Auto-send is also enabled and will fire on its own — this is for testing or re-sends.)`
                  )) return;
                  try {
                    const res = await sendDigestNow(id, week.week_number, true);
                    if (res.ok) toast.success(`Email sent to ${athlete.email}`);
                    else toast.error(res.reason || "Couldn't send");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't send");
                  }
                }}
                data-testid="send-digest-now-btn"
                className="inline-flex items-center gap-2 border border-[var(--tir-navy)] text-[var(--tir-navy)] bg-white px-4 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-navy)] hover:text-white transition-colors"
                title="Send the weekly preview email to the athlete right now"
              >
                <Mail size={14} /> Email week preview
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                if (dirty) await persist();
                navigate(`/coaching/programs/${id}/report`);
              }}
              data-testid="view-full-report-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-5 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <FileText size={14} /> Full program PDF
            </button>
            <button
              type="button"
              onClick={async () => {
                if (dirty) await persist();
                navigate(
                  `/coaching/programs/${id}/weeks/${week.week_number}/report`
                );
              }}
              data-testid="view-week-report-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <FileText size={14} /> Week PDF
            </button>
          </div>
        </>
      )}

      <SaveAsTemplateDialog
        open={showSaveTemplate}
        onOpenChange={setShowSaveTemplate}
        program={program}
        onSaved={(created) => {
          toast(`Open it in your library?`, {
            action: { label: "Open library", onClick: () => navigate(`/coaching/programs/${created.id}/edit`) },
          });
        }}
      />
      {showAssign && (
        <AssignProgramModal
          program={program}
          onClose={() => setShowAssign(false)}
          onAssigned={(newProgram) => {
            setShowAssign(false);
            toast.success(`Assigned to ${newProgram.title}`);
            navigate(`/coaching/programs/${newProgram.id}/edit`);
          }}
        />
      )}
    </div>
  );
}

/**
 * IntroPagesSection — collapsible "PDF intro pages" block.
 *
 * The coach lands on the program editor mostly to plan the week, not
 * to read 600 words of intro every visit. Default state is collapsed
 * when the program already has intro content; expanded when both
 * fields are empty (new program, the coach needs to fill it in).
 *
 * Explicit user choice is persisted per-program in localStorage so
 * the section stays the way they left it.
 */
function IntroPagesSection({ program, updateProgramField }) {
  const storageKey = `tir-intro-pages-expanded:${program?.id || "new"}`;
  const hasContent = !!(
    (program?.intro_howto || "").trim() || (program?.intro_modify || "").trim()
  );
  const [expanded, setExpanded] = React.useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === "1") return true;
      if (saved === "0") return false;
    } catch { /* ignore */ }
    // No saved preference → expand only for empty (new) programs.
    return !hasContent;
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, expanded ? "1" : "0");
    } catch { /* ignore */ }
  }, [expanded, storageKey]);
  return (
    <div data-testid="intro-pages-section">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left group"
        data-testid="intro-pages-toggle"
        aria-expanded={expanded}
      >
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold inline-flex items-center gap-2">
          <ChevronRight
            size={12}
            className={`text-[var(--tir-burgundy)] transition-transform ${expanded ? "rotate-90" : ""}`}
          />
          PDF intro pages
          {hasContent && !expanded && (
            <span className="normal-case tracking-normal text-[10px] text-[var(--tir-burgundy)] font-semibold not-italic">
              · filled in
            </span>
          )}
          <span className="ml-1 normal-case tracking-normal text-[10px] text-[var(--tir-muted)]/70 font-normal italic">
            — leave blank to skip the page in the Full Program PDF
          </span>
        </div>
        <span className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] group-hover:text-[var(--tir-burgundy-dark)]">
          {expanded ? "Collapse" : "Expand"}
        </span>
      </button>
      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Intro · How to use this program
            </label>
            <RichTextEditor
              value={program.intro_howto || ""}
              onChange={(v) => updateProgramField("intro_howto", v)}
              rows={4}
              testid="intro-howto-input"
              placeholder="Explain the flow of the plan, volume progression, what each session is for…"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Intro · How to modify
            </label>
            <RichTextEditor
              value={program.intro_modify || ""}
              onChange={(v) => updateProgramField("intro_modify", v)}
              rows={4}
              testid="intro-modify-input"
              placeholder="Swap rules, fallback options, what to do when life happens…"
            />
          </div>
        </div>
      )}
    </div>
  );
}


// Day labels for the per-day include/exclude toggles below. Kept in this
// file (not imported from coaching lib) because coach-side week days can
// be relabeled per program and we always want the actual weekday order.
const STEP_GOAL_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * StepGoalControls — coach-facing controls for the per-week daily step
 * goal. When enabled, a step-count chip appears at the bottom of every
 * day cell on the athlete's calendar; each day can be individually
 * excluded (race day, sick day, etc.) via the 7 toggle chips below.
 *
 * Data shape (stored on `week.step_goal`):
 *   { enabled: boolean, target: number, excluded_days: number[] }
 * where excluded_days is a list of day indices 0..6 (Mon=0).
 */
function StepGoalControls({ week, onChange }) {
  const sg = week.step_goal || { enabled: false, target: 10000, excluded_days: [], day_targets: {} };
  const excluded = new Set(sg.excluded_days || []);
  const dayTargets = sg.day_targets || {};
  const patch = (delta) => onChange({ ...sg, ...delta });
  const toggleDay = (i) => {
    const next = new Set(excluded);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    patch({ excluded_days: Array.from(next).sort((a, b) => a - b) });
  };
  const setDayTarget = (i, rawValue) => {
    // rawValue may be "", null, or a number string. Empty clears the
    // override — day falls back to the week default.
    const next = { ...dayTargets };
    const trimmed = String(rawValue ?? "").trim();
    if (trimmed === "") {
      delete next[String(i)];
    } else {
      const n = parseInt(trimmed, 10);
      if (Number.isFinite(n) && n >= 0) next[String(i)] = n;
    }
    patch({ day_targets: next });
  };
  return (
    <div
      className="md:col-span-3 border-t border-[var(--tir-border)] pt-3 mt-1"
      data-testid="step-goal-controls"
    >
      <div className="flex flex-wrap items-center gap-4">
        <label
          className="inline-flex items-center gap-2 text-sm text-[var(--tir-ink)] cursor-pointer"
          title="Turn on to auto-append a daily step-count chip to every day of this week on the athlete portal. They tap it to log actual steps + mark complete/missed."
        >
          <input
            type="checkbox"
            checked={!!sg.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
            data-testid="step-goal-enabled-toggle"
            className="h-4 w-4 accent-[var(--tir-burgundy)]"
          />
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
            Daily step goal
          </span>
        </label>
        {sg.enabled && (
          <label className="inline-flex items-center gap-2 text-sm text-[var(--tir-ink)]">
            <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              Default target
            </span>
            <input
              type="number"
              min={0}
              step={500}
              value={sg.target ?? 10000}
              onChange={(e) => {
                const n = parseInt(e.target.value || "0", 10);
                patch({ target: Number.isFinite(n) && n >= 0 ? n : 0 });
              }}
              data-testid="step-goal-target-input"
              className="w-24 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 font-mono-tir text-base font-semibold focus:outline-none"
            />
            <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
              steps / day
            </span>
          </label>
        )}
      </div>
      {sg.enabled && (
        <div className="mt-3">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
            Per-day override
            <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--tir-muted)]/70 font-normal italic">
              — tap the day label to skip · leave the input blank to use the default
            </span>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {STEP_GOAL_DAY_LABELS.map((label, i) => {
              const off = excluded.has(i);
              const overrideVal = dayTargets[String(i)];
              return (
                <div key={i} className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => toggleDay(i)}
                    data-testid={`step-goal-day-toggle-${i}`}
                    className={
                      "px-2 py-1 text-[10px] tracking-[0.14em] uppercase font-semibold border rounded-sm transition-colors text-center " +
                      (off
                        ? "border-dashed border-[var(--tir-border)] text-[var(--tir-muted)] line-through bg-[var(--tir-cream)]/40"
                        : "border-[var(--tir-burgundy)] text-[var(--tir-burgundy)] bg-[var(--tir-burgundy)]/5")
                    }
                    aria-pressed={!off}
                    title={off ? `Skip ${label} — no chip on this day` : `Show step chip on ${label} — click to skip`}
                  >
                    {label}
                  </button>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    disabled={off}
                    value={overrideVal ?? ""}
                    placeholder={String(sg.target ?? 10000)}
                    onChange={(e) => setDayTarget(i, e.target.value)}
                    data-testid={`step-goal-day-target-${i}`}
                    title={
                      off
                        ? `${label} is skipped this week`
                        : `Custom target for ${label} — blank uses the ${sg.target ?? 10000} default`
                    }
                    className={
                      "w-full bg-transparent border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] " +
                      "px-1.5 py-1 font-mono-tir text-xs text-center rounded-sm focus:outline-none " +
                      (off ? "opacity-40 cursor-not-allowed" : "")
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
